/**
 * Observability — frontend Sentry/Logflare wiring barrel.
 *
 * Phase 5 (autonomous PLAN id; roadmap Phase 3). Ships a thin abstraction so
 * the rest of the codebase can call `captureError(err)` / `captureMessage(...)`
 * without depending on the Sentry SDK directly. That lets us:
 *
 *   - Swap Sentry for Logflare or any other vendor in one file
 *   - Stub the observability calls in tests
 *   - No-op cleanly in dev / when DSN missing (so dev consoles are not
 *     polluted with "Sentry not configured" warnings)
 *
 * ## Wiring
 *
 * `@sentry/react` IS installed and wired in `src/main.tsx`, but behind the
 * `VITE_SENTRY_DSN` build-time flag: with a DSN set, `SentryObservability`
 * (see `sentry-impl.ts`) is initialized; without one this barrel's no-op
 * default impl stays active and the Sentry SDK is tree-shaken out entirely
 * (0 bytes - verified in the production build). Activating Sentry in prod is
 * therefore just a matter of setting `VITE_SENTRY_DSN` at build time.
 *
 * The edge-function side (Deno Sentry SDK) is still pending - see
 * `TODO.md#phase-5-sentry-install`.
 */

export interface ErrorContext {
  /** Free-form tags ('phase=3', 'edge_function=verify-password', ...). */
  tags?: Record<string, string>;
  /** Arbitrary contextual data, JSON-serializable. */
  extra?: Record<string, unknown>;
  /** Optional user pseudo-id (never PII — use hashed subject). */
  userPseudoId?: string;
}

export interface Observability {
  /** Capture a thrown Error. */
  captureError(err: unknown, ctx?: ErrorContext): void;
  /** Capture a string message (e.g. "JIT lazy-upsert hit rate elevated"). */
  captureMessage(msg: string, ctx?: ErrorContext): void;
  /** Add a breadcrumb for the next event. */
  addBreadcrumb(category: string, message: string, data?: Record<string, unknown>): void;
}

class NoopObservability implements Observability {
  captureError(_err: unknown, _ctx?: ErrorContext): void {
    void _err;
    void _ctx;
  }
  captureMessage(_msg: string, _ctx?: ErrorContext): void {
    void _msg;
    void _ctx;
  }
  addBreadcrumb(_category: string, _message: string, _data?: Record<string, unknown>): void {
    void _category;
    void _message;
    void _data;
  }
}

// Module-singleton. `initObservability(...)` swaps it in `src/main.tsx`.
let _impl: Observability = new NoopObservability();

export function initObservability(impl: Observability): void {
  _impl = impl;
}

export function captureError(err: unknown, ctx?: ErrorContext): void {
  _impl.captureError(err, ctx);
}

export function captureMessage(msg: string, ctx?: ErrorContext): void {
  _impl.captureMessage(msg, ctx);
}

export function addBreadcrumb(
  category: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  _impl.addBreadcrumb(category, message, data);
}
